// `configureAdsData` — the injected-config seam. Packages are variant-agnostic
// and store-free: the app injects its reactive auth-token hook, its shared
// QueryClient, its notifications query hook and the cached audience signals
// at bootstrap (`src/composition/hosts/ads/ads-data-wiring.ts`). Transport is NOT part of this
// seam — `data/hcp-verification` reads the app's configured clients from the
// `@heidi/api/client` registry. Mirrors the commerce and workspace seams.

import type { UserResponse } from '@heidi/workspace/contracts';
import { type QueryClient } from '@tanstack/react-query';

/**
 * The notifications row as the inmail sync reads it — every field optional so
 * the app's `UserInmailResponse` rows assign without a cast.
 */
export interface AdsDataInmail {
  id?: string | null;
  notification_name?: string | null;
  title?: string | null;
  created_at?: string | null;
}

/**
 * What the app's caches say about the viewer, each `undefined` until the cache
 * that answers it has loaded. The placement request is withheld only on a
 * positive answer — `isOnPremBuild: true`, `isInAdCountry: false`,
 * `isManaged: true`, `isInTeam: true` or `isFreeTier: false` — so a cold
 * cache never silences a slot. The server's own deployment and audience checks
 * stay the decision; this only saves the round trip whose answer the client
 * can already see. The deployment region is not mirrored: the country already
 * keeps the request off every non-US viewer, and the server refuses the rest.
 */
export interface AdAudienceSignals {
  /** The app is the on-prem build, which never holds ad data. */
  isOnPremBuild?: boolean;
  /**
   * The viewer's account country is one ads are shown to. `false` means the
   * user record has loaded with another, or no, country.
   */
  isInAdCountry?: boolean;
  /** Managed on the viewer's own record. */
  isManaged?: boolean;
  /** Member of at least one team. */
  isInTeam?: boolean;
  /** On the FREE product tier. `false` means a paid tier is known. */
  isFreeTier?: boolean;
}

/** The injected app config for `@heidi/ads/data`. */
export interface AdsDataConfig {
  /**
   * The reactive auth token, injected as a HOOK so gated queries re-evaluate
   * on token refresh instead of freezing the first-render value.
   */
  useAuthToken: () => string | null | undefined;
  /**
   * The app's shared QueryClient, so module-scope cache commands
   * (`invalidateQueries`) hit the SAME cache the app renders from.
   */
  queryClient: QueryClient;
  /**
   * The app's notifications query hook, by reference — the inmail sync only
   * reads `data`, so the minimal result shape is all this type names.
   */
  useQueryNotifications: () => { data?: readonly AdsDataInmail[] };
  /**
   * The app's `useQueryUser` hook, injected whole — a package data layer may
   * not import a foreign domain's /data door, so the user gate crosses here.
   * Only `data` is read, so the minimal result shape is all this type names.
   */
  useQueryUser: <T>(select?: (user: UserResponse | undefined) => T) => { data?: T };
  /**
   * Cached audience signals, injected as a HOOK so a mounted placement
   * re-reads them as the user, team and billing caches load.
   * Workspace and commerce own those caches; this package may not reach them
   * from `/data`.
   */
  useAudienceSignals: () => AdAudienceSignals;
}

let config: AdsDataConfig | null = null;

export const configureAdsData = (next: AdsDataConfig): void => {
  config = next;
};

/** Fails loudly rather than silently degrading — the workspace/commerce rule. */
export const getAdsDataConfig = (): AdsDataConfig => {
  if (!config) {
    throw new Error(
      '[ads/data] not configured — see src/composition/hosts/ads/ads-data-wiring.ts'
    );
  }
  return config;
};

export const resetAdsDataConfigForTests = (): void => {
  config = null;
};
